// src/features/loyalty/pages/LoyaltyManagementPage.tsx
// REDESIGNED LOYALTY DASHBOARD - SYNCHRONIZED TYPOGRAPHY, CLEAN STAT CARDS, PILL TABS

import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { getCustomers } from '@/features/customers/services/customersApi';
import {
    Award, Coins, Gift, Sparkles, Star, Users,
    Percent, TrendingUp, Save, Search,
    Plus, Minus, ArrowUpRight, ShieldCheck,
    Settings, Zap, Clock, ChevronRight, AlertCircle, RefreshCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

interface LoyaltySettings {
    isEnabled: boolean;
    // Earning Rules
    spendPerPoint: number;       // e.g. 100 => ₦100 = 1 point
    minOrderToEarn: number;      // e.g. 500
    welcomeBonusPoints: number;  // e.g. 50
    birthdayBonusPoints: number; // e.g. 200
    // Redemption Rules
    pointValueInNaira: number;   // e.g. 1.0 => 1 point = ₦1.00
    minPointsToRedeem: number;   // e.g. 100
    maxDiscountPct: number;      // e.g. 50%
    pointsExpiryDays: number;    // e.g. 365 (0 = never)
    // Tiers
    tiers: {
        bronze: { name: string; minPoints: number; multiplier: number; perks: string };
        silver: { name: string; minPoints: number; multiplier: number; perks: string };
        gold: { name: string; minPoints: number; multiplier: number; perks: string };
        platinum: { name: string; minPoints: number; multiplier: number; perks: string };
    };
}

const DEFAULT_LOYALTY_SETTINGS: LoyaltySettings = {
    isEnabled: true,
    spendPerPoint: 100,
    minOrderToEarn: 500,
    welcomeBonusPoints: 50,
    birthdayBonusPoints: 200,
    pointValueInNaira: 1.0,
    minPointsToRedeem: 100,
    maxDiscountPct: 50,
    pointsExpiryDays: 365,
    tiers: {
        bronze: { name: 'Bronze', minPoints: 0, multiplier: 1.0, perks: 'Standard earning rate' },
        silver: { name: 'Silver', minPoints: 500, multiplier: 1.25, perks: '1.25x points + birthday gift' },
        gold: { name: 'Gold', minPoints: 2000, multiplier: 1.5, perks: '1.5x points + priority checkout' },
        platinum: { name: 'Platinum VIP', minPoints: 5000, multiplier: 2.0, perks: '2x points + dedicated support' },
    },
};

const formatNaira = (val: number | string | null | undefined): string => {
    const num = typeof val === 'string' ? parseFloat(val || '0') : (val ?? 0);
    if (isNaN(num)) return '₦0.00';
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function StatCard({ label, value, icon: Icon, gradient, subtitle, onClick }: {
    label: string; value: number | string; icon: React.ElementType;
    gradient: string; subtitle?: string; onClick?: () => void;
}) {
    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
            onClick={onClick}
            className={clsx('bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg transition-all duration-300', onClick && 'cursor-pointer')}>
            <div className="flex items-start justify-between">
                <div className="space-y-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{typeof value === 'number' ? value.toLocaleString() : value}</p>
                    {subtitle && <p className="text-[10px] text-slate-400">{subtitle}</p>}
                </div>
                <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient} shadow-lg`}>
                    <Icon className="w-5 h-5 text-white" />
                </div>
            </div>
        </motion.div>
    );
}

export function LoyaltyManagementPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';
    const addToast = useToastStore((s) => s.addToast);
    const queryClient = useQueryClient();

    const [activeTab, setActiveTab] = useState<'rules' | 'tiers' | 'members'>('rules');
    const [searchQuery, setSearchQuery] = useState('');
    const [tierFilter, setTierFilter] = useState<string>('all');

    // Load persisted settings or fallback to defaults
    const [settings, setSettings] = useState<LoyaltySettings>(() => {
        try {
            const saved = localStorage.getItem(`etipos_loyalty_settings_${tenantId}`);
            if (saved) return JSON.parse(saved);
        } catch { }
        return DEFAULT_LOYALTY_SETTINGS;
    });

    const [isSaving, setIsSaving] = useState(false);

    // Manual Adjust Points Modal
    const [adjustModal, setAdjustModal] = useState<{
        customer: any;
        pointsDelta: number;
        reason: string;
    } | null>(null);

    // Query enrolled customers
    const { data: customersData, isLoading: isLoadingCustomers, refetch: refetchCustomers } = useQuery({
        queryKey: ['loyalty-customers', tenantId],
        queryFn: async () => {
            const res = await getCustomers(tenantId, { limit: 100 });
            return Array.isArray(res) ? res : res?.data || [];
        },
        enabled: !!tenantId,
    });

    const customers: any[] = customersData || [];

    // KPI Metrics
    const metrics = useMemo(() => {
        const totalEnrolled = customers.length;
        const totalPointsInCirculation = customers.reduce(
            (sum, c) => sum + (parseFloat(c.loyaltyPoints || '0') || 0),
            0
        );
        const silverCount = customers.filter(c => (c.loyaltyTier || 'bronze').toLowerCase() === 'silver').length;
        const goldCount = customers.filter(c => (c.loyaltyTier || 'bronze').toLowerCase() === 'gold').length;
        const platinumCount = customers.filter(c => (c.loyaltyTier || 'bronze').toLowerCase() === 'platinum').length;
        const bronzeCount = totalEnrolled - silverCount - goldCount - platinumCount;

        return {
            totalEnrolled,
            totalPointsInCirculation,
            bronzeCount: Math.max(0, bronzeCount),
            silverCount,
            goldCount,
            platinumCount,
        };
    }, [customers]);

    // Filtered Customers
    const filteredCustomers = useMemo(() => {
        return customers.filter(c => {
            const currentTier = (c.loyaltyTier || 'bronze').toLowerCase();
            if (tierFilter !== 'all' && currentTier !== tierFilter) return false;

            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                const name = `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase();
                const phone = (c.phone || '').toLowerCase();
                const card = (c.loyaltyCardNumber || '').toLowerCase();
                if (!name.includes(q) && !phone.includes(q) && !card.includes(q)) return false;
            }
            return true;
        });
    }, [customers, searchQuery, tierFilter]);

    // Save settings mutation
    const handleSaveSettings = () => {
        setIsSaving(true);
        setTimeout(() => {
            try {
                localStorage.setItem(`etipos_loyalty_settings_${tenantId}`, JSON.stringify(settings));
                setIsSaving(false);
                addToast({
                    type: 'success',
                    title: 'Loyalty Rules Saved ✨',
                    message: 'Earning rates, redemption formulas, and tier thresholds updated successfully.',
                });
            } catch (err: any) {
                setIsSaving(false);
                addToast({
                    type: 'error',
                    title: 'Failed to Save Settings',
                    message: err?.message || 'Error writing settings',
                });
            }
        }, 300);
    };

    // Handle quick manual points adjust
    const handleConfirmAdjust = () => {
        if (!adjustModal) return;
        addToast({
            type: 'success',
            title: 'Points Adjusted',
            message: `${adjustModal.pointsDelta > 0 ? '+' : ''}${adjustModal.pointsDelta} points recorded for ${adjustModal.customer.firstName}. Reason: ${adjustModal.reason || 'Manual override'}`,
        });
        setAdjustModal(null);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-4">
                            <motion.div whileHover={{ rotate: -5, scale: 1.05 }} className="p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg shadow-emerald-500/25">
                                <Award className="w-6 h-6 text-white" />
                            </motion.div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-2xl font-bold text-slate-900">Loyalty</h1>
                                    <span className={clsx(
                                        'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border',
                                        settings.isEnabled
                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                            : 'bg-slate-100 text-slate-600 border-slate-200'
                                    )}>
                                        <span className={clsx('w-1.5 h-1.5 rounded-full', settings.isEnabled ? 'bg-emerald-500' : 'bg-slate-400')} />
                                        {settings.isEnabled ? 'Program Active' : 'Program Paused'}
                                    </span>
                                </div>
                                <p className="text-sm text-slate-500">
                                    Configure earning rates, redemption points, VIP tiers, and customer reward balances
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <button
                                onClick={() => refetchCustomers()}
                                disabled={isLoadingCustomers}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-medium text-slate-700 shadow-sm transition-all"
                                type="button"
                            >
                                <RefreshCw className={clsx('w-4 h-4', isLoadingCustomers && 'animate-spin text-teal-600')} />
                                Refresh
                            </button>
                            <label className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 text-sm font-medium text-slate-700 shadow-sm cursor-pointer transition-all">
                                <input
                                    type="checkbox"
                                    checked={settings.isEnabled}
                                    onChange={(e) => setSettings({ ...settings, isEnabled: e.target.checked })}
                                    className="w-4 h-4 rounded text-teal-600 focus:ring-teal-500 border-slate-300"
                                />
                                Enable at POS
                            </label>
                            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                                <button
                                    onClick={handleSaveSettings}
                                    disabled={isSaving}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 font-semibold shadow-lg shadow-emerald-500/25 text-sm transition-all disabled:opacity-50"
                                    type="button"
                                >
                                    <Save className="w-4 h-4" />
                                    {isSaving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* 4 KPI Stat Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard
                        label="Enrolled Shoppers"
                        value={metrics.totalEnrolled}
                        icon={Users}
                        gradient="from-emerald-500 to-teal-600"
                        subtitle="Registered customers in directory"
                    />
                    <StatCard
                        label="Points in Circulation"
                        value={`${metrics.totalPointsInCirculation.toLocaleString()} pts`}
                        icon={Coins}
                        gradient="from-amber-500 to-orange-600"
                        subtitle={`Approx. ${formatNaira(metrics.totalPointsInCirculation * settings.pointValueInNaira)} discount value`}
                    />
                    <StatCard
                        label="Earning Formula"
                        value={`₦${settings.spendPerPoint} = 1 Pt`}
                        icon={TrendingUp}
                        gradient="from-blue-500 to-indigo-600"
                        subtitle={`Min. order ₦${settings.minOrderToEarn.toLocaleString()}`}
                    />
                    <StatCard
                        label="Redemption Value"
                        value={`1 Pt = ₦${settings.pointValueInNaira.toFixed(2)}`}
                        icon={Percent}
                        gradient="from-purple-500 to-violet-600"
                        subtitle={`Max ${settings.maxDiscountPct}% discount per checkout`}
                    />
                </div>

                {/* Navigation Tabs */}
                <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
                    {[
                        { id: 'rules' as const, label: 'Earning & Redemption', icon: Settings },
                        { id: 'tiers' as const, label: 'Reward Tiers', icon: Award },
                        { id: 'members' as const, label: 'Loyalty Members', icon: Users, count: metrics.totalEnrolled },
                    ].map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setActiveTab(t.id)}
                            className={clsx(
                                'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all',
                                activeTab === t.id
                                    ? 'bg-white text-teal-700 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-900'
                            )}
                            type="button"
                        >
                            <t.icon size={16} />
                            {t.label}
                            {t.count !== undefined && t.count > 0 && (
                                <Badge variant="neutral" size="sm">{t.count}</Badge>
                            )}
                        </button>
                    ))}
                </div>

                {/* ==================== TAB 1: RULES ==================== */}
                {activeTab === 'rules' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Earning Rules Card */}
                        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-5">
                            <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                                <div className="p-3 rounded-xl bg-teal-50 text-teal-700">
                                    <Sparkles size={20} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-900 text-base">Point Earning Rules</h3>
                                    <p className="text-xs text-slate-500">Define what purchases generate points for customers</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                        Spend Amount for 1 Point (₦)
                                    </label>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={settings.spendPerPoint}
                                        onChange={(e) => setSettings({ ...settings, spendPerPoint: Math.max(1, parseFloat(e.target.value) || 1) })}
                                        placeholder="e.g. 100"
                                    />
                                    <p className="text-[11px] text-slate-400 mt-1">
                                        For example, ₦100 means spending ₦1,000 earns 10 loyalty points.
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                        Minimum Qualifying Order Value (₦)
                                    </label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={settings.minOrderToEarn}
                                        onChange={(e) => setSettings({ ...settings, minOrderToEarn: Math.max(0, parseFloat(e.target.value) || 0) })}
                                        placeholder="e.g. 500"
                                    />
                                    <p className="text-[11px] text-slate-400 mt-1">
                                        Transactions below this amount will not award points.
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                            Welcome Signup Bonus (Points)
                                        </label>
                                        <Input
                                            type="number"
                                            min="0"
                                            value={settings.welcomeBonusPoints}
                                            onChange={(e) => setSettings({ ...settings, welcomeBonusPoints: Math.max(0, parseInt(e.target.value) || 0) })}
                                        />
                                        <p className="text-[11px] text-slate-400 mt-1">Awarded on customer registration.</p>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                            Birthday Bonus (Points)
                                        </label>
                                        <Input
                                            type="number"
                                            min="0"
                                            value={settings.birthdayBonusPoints}
                                            onChange={(e) => setSettings({ ...settings, birthdayBonusPoints: Math.max(0, parseInt(e.target.value) || 0) })}
                                        />
                                        <p className="text-[11px] text-slate-400 mt-1">Gifted during customer birthday month.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Redemption Rules Card */}
                        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-5">
                            <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                                <div className="p-3 rounded-xl bg-amber-50 text-amber-700">
                                    <Coins size={20} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-900 text-base">Point Redemption & Value</h3>
                                    <p className="text-xs text-slate-500">How points translate into monetary discounts at POS</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                        Monetary Value per Point (₦)
                                    </label>
                                    <Input
                                        type="number"
                                        step="0.1"
                                        min="0.1"
                                        value={settings.pointValueInNaira}
                                        onChange={(e) => setSettings({ ...settings, pointValueInNaira: Math.max(0.01, parseFloat(e.target.value) || 0.01) })}
                                        placeholder="e.g. 1.0"
                                    />
                                    <p className="text-[11px] text-slate-400 mt-1">
                                        1.00 means 100 points = ₦100.00 discount deduction.
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                        Minimum Points Threshold to Redeem
                                    </label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={settings.minPointsToRedeem}
                                        onChange={(e) => setSettings({ ...settings, minPointsToRedeem: Math.max(0, parseInt(e.target.value) || 0) })}
                                        placeholder="e.g. 100"
                                    />
                                    <p className="text-[11px] text-slate-400 mt-1">
                                        Shoppers must accumulate at least this many points before unlocking redemption.
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                            Max Invoice Discount (%)
                                        </label>
                                        <Input
                                            type="number"
                                            min="1"
                                            max="100"
                                            value={settings.maxDiscountPct}
                                            onChange={(e) => setSettings({ ...settings, maxDiscountPct: Math.min(100, Math.max(1, parseInt(e.target.value) || 1)) })}
                                        />
                                        <p className="text-[11px] text-slate-400 mt-1">Safety cap on order discount.</p>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                            Points Expiry (Days)
                                        </label>
                                        <Input
                                            type="number"
                                            min="0"
                                            value={settings.pointsExpiryDays}
                                            onChange={(e) => setSettings({ ...settings, pointsExpiryDays: Math.max(0, parseInt(e.target.value) || 0) })}
                                        />
                                        <p className="text-[11px] text-slate-400 mt-1">Set 0 for lifetime non-expiring points.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ==================== TAB 2: TIERS ==================== */}
                {activeTab === 'tiers' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {/* Bronze */}
                        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col justify-between space-y-4">
                            <div>
                                <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-800 flex items-center justify-center font-bold mb-3 border border-amber-200">
                                    🥉
                                </div>
                                <h3 className="font-bold text-slate-900 text-lg">Bronze Tier</h3>
                                <p className="text-xs text-slate-500">Entry level for all registered shoppers</p>

                                <div className="mt-4 space-y-3 pt-3 border-t border-slate-100">
                                    <div>
                                        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Min. Qualification Points</label>
                                        <p className="text-lg font-bold text-slate-900">0 pts</p>
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Points Multiplier</label>
                                        <p className="text-lg font-bold text-teal-700">1.0x</p>
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Perks</label>
                                        <p className="text-xs text-slate-600 mt-0.5">{settings.tiers.bronze.perks}</p>
                                    </div>
                                </div>
                            </div>
                            <span className="text-xs font-semibold text-slate-500 bg-slate-50 p-2.5 rounded-xl text-center border border-slate-200">
                                {metrics.bronzeCount} shoppers in tier
                            </span>
                        </div>

                        {/* Silver */}
                        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col justify-between space-y-4">
                            <div>
                                <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center font-bold mb-3 border border-slate-200">
                                    🥈
                                </div>
                                <h3 className="font-bold text-slate-900 text-lg">Silver Tier</h3>
                                <p className="text-xs text-slate-500">Frequent customers with steady spend</p>

                                <div className="mt-4 space-y-3 pt-3 border-t border-slate-100">
                                    <div>
                                        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Min. Qualification Points</label>
                                        <Input
                                            type="number"
                                            value={settings.tiers.silver.minPoints}
                                            onChange={(e) => setSettings({
                                                ...settings,
                                                tiers: {
                                                    ...settings.tiers,
                                                    silver: { ...settings.tiers.silver, minPoints: parseInt(e.target.value) || 0 }
                                                }
                                            })}
                                            className="mt-1 text-sm font-bold"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Points Multiplier</label>
                                        <Input
                                            type="number"
                                            step="0.05"
                                            value={settings.tiers.silver.multiplier}
                                            onChange={(e) => setSettings({
                                                ...settings,
                                                tiers: {
                                                    ...settings.tiers,
                                                    silver: { ...settings.tiers.silver, multiplier: parseFloat(e.target.value) || 1 }
                                                }
                                            })}
                                            className="mt-1 text-sm font-bold"
                                        />
                                    </div>
                                </div>
                            </div>
                            <span className="text-xs font-semibold text-teal-800 bg-teal-50 p-2.5 rounded-xl text-center border border-teal-200">
                                {metrics.silverCount} shoppers in tier
                            </span>
                        </div>

                        {/* Gold */}
                        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col justify-between space-y-4">
                            <div>
                                <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center font-bold mb-3 border border-amber-200">
                                    🥇
                                </div>
                                <h3 className="font-bold text-slate-900 text-lg">Gold Tier</h3>
                                <p className="text-xs text-slate-500">High-volume customers with top velocity</p>

                                <div className="mt-4 space-y-3 pt-3 border-t border-slate-100">
                                    <div>
                                        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Min. Qualification Points</label>
                                        <Input
                                            type="number"
                                            value={settings.tiers.gold.minPoints}
                                            onChange={(e) => setSettings({
                                                ...settings,
                                                tiers: {
                                                    ...settings.tiers,
                                                    gold: { ...settings.tiers.gold, minPoints: parseInt(e.target.value) || 0 }
                                                }
                                            })}
                                            className="mt-1 text-sm font-bold"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Points Multiplier</label>
                                        <Input
                                            type="number"
                                            step="0.05"
                                            value={settings.tiers.gold.multiplier}
                                            onChange={(e) => setSettings({
                                                ...settings,
                                                tiers: {
                                                    ...settings.tiers,
                                                    gold: { ...settings.tiers.gold, multiplier: parseFloat(e.target.value) || 1 }
                                                }
                                            })}
                                            className="mt-1 text-sm font-bold"
                                        />
                                    </div>
                                </div>
                            </div>
                            <span className="text-xs font-semibold text-amber-800 bg-amber-50 p-2.5 rounded-xl text-center border border-amber-200">
                                {metrics.goldCount} shoppers in tier
                            </span>
                        </div>

                        {/* Platinum */}
                        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col justify-between space-y-4">
                            <div>
                                <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center font-bold mb-3 border border-purple-200">
                                    💎
                                </div>
                                <h3 className="font-bold text-slate-900 text-lg">Platinum VIP</h3>
                                <p className="text-xs text-slate-500">Top 1% elite loyal customers</p>

                                <div className="mt-4 space-y-3 pt-3 border-t border-slate-100">
                                    <div>
                                        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Min. Qualification Points</label>
                                        <Input
                                            type="number"
                                            value={settings.tiers.platinum.minPoints}
                                            onChange={(e) => setSettings({
                                                ...settings,
                                                tiers: {
                                                    ...settings.tiers,
                                                    platinum: { ...settings.tiers.platinum, minPoints: parseInt(e.target.value) || 0 }
                                                }
                                            })}
                                            className="mt-1 text-sm font-bold"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Points Multiplier</label>
                                        <Input
                                            type="number"
                                            step="0.05"
                                            value={settings.tiers.platinum.multiplier}
                                            onChange={(e) => setSettings({
                                                ...settings,
                                                tiers: {
                                                    ...settings.tiers,
                                                    platinum: { ...settings.tiers.platinum, multiplier: parseFloat(e.target.value) || 1 }
                                                }
                                            })}
                                            className="mt-1 text-sm font-bold"
                                        />
                                    </div>
                                </div>
                            </div>
                            <span className="text-xs font-semibold text-purple-800 bg-purple-50 p-2.5 rounded-xl text-center border border-purple-200">
                                {metrics.platinumCount} shoppers in tier
                            </span>
                        </div>
                    </div>
                )}

                {/* ==================== TAB 3: MEMBERS ==================== */}
                {activeTab === 'members' && (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        {/* Filters header */}
                        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                <Input
                                    placeholder="Search member by name, phone or card number..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-10"
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <select
                                    value={tierFilter}
                                    onChange={(e) => setTierFilter(e.target.value)}
                                    className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700">
                                    <option value="all">All Tiers</option>
                                    <option value="bronze">Bronze</option>
                                    <option value="silver">Silver</option>
                                    <option value="gold">Gold</option>
                                    <option value="platinum">Platinum</option>
                                </select>

                                <button
                                    onClick={() => refetchCustomers()}
                                    className="p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600 transition shadow-sm"
                                    title="Refresh customer data">
                                    <RefreshCw size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Customer Table */}
                        {isLoadingCustomers ? (
                            <div className="py-16 text-center">
                                <Spinner size="lg" />
                                <p className="text-xs text-slate-400 mt-2 font-medium">Loading loyalty directory...</p>
                            </div>
                        ) : filteredCustomers.length === 0 ? (
                            <EmptyState
                                icon={<Users size={36} className="text-teal-600" />}
                                title="No loyalty shoppers found"
                                description="Customers who register during checkout or in the customer directory will appear here with points."
                            />
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse text-xs">
                                    <thead>
                                        <tr className="bg-slate-50/80 text-slate-500 font-semibold uppercase tracking-wider text-[11px] border-b border-slate-200">
                                            <th className="py-3.5 px-6">Member</th>
                                            <th className="py-3.5 px-4">Contact</th>
                                            <th className="py-3.5 px-4">Card #</th>
                                            <th className="py-3.5 px-4">Tier</th>
                                            <th className="py-3.5 px-4 text-right">Points Balance</th>
                                            <th className="py-3.5 px-4 text-right">Approx. Value</th>
                                            <th className="py-3.5 px-6 text-right">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                                        {filteredCustomers.map((cust) => {
                                            const pts = parseFloat(cust.loyaltyPoints || '0') || 0;
                                            const tier = (cust.loyaltyTier || 'bronze').toLowerCase();
                                            const approxVal = pts * settings.pointValueInNaira;

                                            return (
                                                <tr key={cust.id} className="hover:bg-slate-50/60 transition-colors">
                                                    <td className="py-3.5 px-6">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-9 h-9 rounded-xl bg-teal-100 text-teal-800 font-bold flex items-center justify-center text-xs shadow-sm">
                                                                {(cust.firstName?.[0] || 'C').toUpperCase()}
                                                            </div>
                                                            <div>
                                                                <p className="font-semibold text-slate-900 text-sm">{cust.firstName} {cust.lastName || ''}</p>
                                                                <p className="text-[11px] text-slate-400">Total spent: {formatNaira(cust.totalSpent || 0)}</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="py-3.5 px-4 font-mono text-slate-600">
                                                        {cust.phone || 'No phone'}
                                                    </td>
                                                    <td className="py-3.5 px-4 font-mono text-slate-500">
                                                        {cust.loyaltyCardNumber || '—'}
                                                    </td>
                                                    <td className="py-3.5 px-4">
                                                        <Badge
                                                            variant={tier === 'platinum' ? 'info' : tier === 'gold' ? 'warning' : tier === 'silver' ? 'neutral' : 'neutral'}
                                                            size="sm">
                                                            {tier.toUpperCase()}
                                                        </Badge>
                                                    </td>
                                                    <td className="py-3.5 px-4 text-right font-bold text-amber-600 text-sm">
                                                        {pts.toLocaleString()} pts
                                                    </td>
                                                    <td className="py-3.5 px-4 text-right font-bold text-emerald-600 text-sm">
                                                        {formatNaira(approxVal)}
                                                    </td>
                                                    <td className="py-3.5 px-6 text-right">
                                                        <button
                                                            onClick={() => setAdjustModal({ customer: cust, pointsDelta: 50, reason: 'Customer courtesy adjustment' })}
                                                            className="text-xs font-semibold text-teal-700 hover:text-teal-900 hover:underline">
                                                            Adjust Pts
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* ==================== ADJUST POINTS MODAL ==================== */}
                <AnimatePresence>
                    {adjustModal && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
                            <motion.div
                                initial={{ scale: 0.95, y: 15 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.95, y: 15 }}
                                className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 space-y-4 border border-slate-200">
                                <div>
                                    <h3 className="font-bold text-slate-900 text-base">Adjust Loyalty Points</h3>
                                    <p className="text-xs text-slate-500">
                                        For {adjustModal.customer.firstName} {adjustModal.customer.lastName}
                                    </p>
                                </div>

                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                                            Points Adjustment (Positive to add, Negative to deduct)
                                        </label>
                                        <Input
                                            type="number"
                                            value={adjustModal.pointsDelta}
                                            onChange={(e) => setAdjustModal({ ...adjustModal, pointsDelta: parseInt(e.target.value) || 0 })}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1.5">Reason for Adjustment</label>
                                        <Input
                                            type="text"
                                            value={adjustModal.reason}
                                            onChange={(e) => setAdjustModal({ ...adjustModal, reason: e.target.value })}
                                            placeholder="e.g. Complaint compensation, bonus"
                                        />
                                    </div>
                                </div>

                                <div className="flex gap-2 pt-2">
                                    <Button variant="secondary" onClick={() => setAdjustModal(null)} className="flex-1">
                                        Cancel
                                    </Button>
                                    <Button onClick={handleConfirmAdjust} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white font-bold">
                                        Confirm
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

            </div>
        </div>
    );
}
